import * as Yup from 'yup';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  InputLabel,
  Typography,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  CardHeader
} from '@mui/material';
import { Tab } from '@mui/material';
import { axios } from 'utils/axios';
import ContactSites from 'pages/dashboard/contact-sites/ContactSites';
import { FormHelperText } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ContactIcons } from 'utils/Icons/icons';
import InfoCard from 'components/common/InfoCard';

type CompanyNewFormProps = {
  isEdit: boolean;
  isDetails: boolean;
  currentUser?: any;
};

export default function CompanyCreateForm({ isEdit, isDetails, currentUser }: CompanyNewFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { enqueueSnackbar } = useSnackbar();
  console.log(currentUser, 'currentUser');

  // Validation Schema
  const NewUserSchema = Yup.object().shape({
    companyName: Yup.string().required('Company Name is required'),
    companyReference: Yup.string().required('Company Reference is required'),
    defaultCompany: Yup.boolean().required('Default Company is required'),
    disposalSite: Yup.boolean().required('Disposal Site is required'),
    buildingNumber: Yup.string().required('Building Number is required'),
    addressLine1: Yup.string().required('Address Line 1 is required'),
    addressLine2: Yup.string().nullable(),
    postcode: Yup.string().required('Postcode is required'),
    mainTelephone: Yup.string().required('Main Telephone is required'),
    alternativeTelephone: Yup.string().nullable(),
    alternativeTelephoneDescription: Yup.string().nullable(),
    mobileNumber: Yup.string().required('Mobile Number is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    website: Yup.string().required('Website is required'),
    mainContact: Yup.string().required('Main Contact is required'),
    vatRegistrationNumber: Yup.string().nullable(),
    companyRegistrationNumber: Yup.string().nullable(),
    wasteCarrierNumber: Yup.string().nullable(),
    wasteBrokerNumber: Yup.string().nullable(),
    ppcLicenseNumber: Yup.string().nullable(),
    siteLicenseNumber: Yup.string().nullable(),
    wasteCollectionPermit: Yup.string().nullable(),
    carrierGB: Yup.string().nullable(),
    wasteManagementLicense: Yup.string().nullable(),
    defaultDisposalSite: Yup.string().nullable(),
    defaultBridge: Yup.string().nullable()
  });

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      companyName: currentUser?.COMPANY_NAME || '',
      companyReference: currentUser?.COMPANY_REFERENCE || '',
      defaultCompany: currentUser?.DEFAULT_COMPANY || false,
      disposalSite: currentUser?.DISPOSAL_SITE || false,
      buildingNumber: currentUser?.BUILDING_NUMBER || '',
      addressLine1: currentUser?.COMPANY_ADDRESS_1 || '',
      addressLine2: currentUser?.COMPANY_ADDRESS_2 || '',
      postcode: currentUser?.COMPANY_POSTCODE || '',
      mainTelephone: currentUser?.MAIN_TELEPHONE || '',
      alternativeTelephone: currentUser?.ALT_TELEPHONE || '',
      alternativeTelephoneDescription: currentUser?.ALT_TELEPHONE_DESC || '',
      mobileNumber: currentUser?.MAIN_MOBILE || '',
      email: currentUser?.MAIN_EMAIL || '',
      website: currentUser?.MAIN_WEB || '',
      mainContact: currentUser?.MAIN_CONTACT || '',
      vatRegistrationNumber: currentUser?.VAT_REG_NUMBER || '',
      companyRegistrationNumber: currentUser?.COMPANY_REG_NUMBER || '',
      wasteCarrierNumber: currentUser?.WASTE_CARRIER_NUMBER || '',
      wasteBrokerNumber: currentUser?.WASTE_BROKER_NUMBER || '',
      ppcLicenseNumber: currentUser?.PPC_LICENSE_NUMBER || '',
      siteLicenseNumber: currentUser?.SITE_LICENSE_NUMBER || '',
      wasteCollectionPermit: currentUser?.WASTE_COLLECTION_PERMIT || '',
      carrierGB: currentUser?.CARRIER_GB || '',
      wasteManagementLicense: currentUser?.WASTE_MANAGEMENT_LICENCE || '',
      defaultDisposalSite: currentUser?.DEFAULT_DISPOSAL_SITE || '',
      defaultBridge: currentUser?.DEFAULT_BRIDGE || ''
    },
    validationSchema: isDetails ? null : NewUserSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) {
        enqueueSnackbar('Please correct the errors before submitting.', { variant: 'error' });
        setSubmitting(false);
        return;
      }
      try {
        if (!isDetails) {
          const payload = {
            COMPANY_NAME: values.companyName,
            COMPANY_REFERENCE: values.companyReference,
            DEFAULT_COMPANY: values.defaultCompany,
            DISPOSAL_SITE: values.disposalSite,
            BUILDING_NUMBER: values.buildingNumber,
            COMPANY_ADDRESS_1: values.addressLine1,
            COMPANY_ADDRESS_2: values.addressLine2,
            COMPANY_POSTCODE: values.postcode,
            MAIN_TELEPHONE: values.mainTelephone,
            ALT_TELEPHONE: values.alternativeTelephone,
            ALT_TELEPHONE_DESC: values.alternativeTelephoneDescription,
            MAIN_MOBILE: values.mobileNumber,
            MAIN_EMAIL: values.email,
            MAIN_WEB: values.website,
            MAIN_CONTACT: values.mainContact,
            VAT_REG_NUMBER: values.vatRegistrationNumber,
            COMPANY_REG_NUMBER: values.companyRegistrationNumber,
            WASTE_CARRIER_NUMBER: values.wasteCarrierNumber,
            WASTE_BROKER_NUMBER: values.wasteBrokerNumber,
            PPC_LICENSE_NUMBER: values.ppcLicenseNumber,
            SITE_LICENSE_NUMBER: values.siteLicenseNumber,
            WASTE_COLLECTION_PERMIT: values.wasteCollectionPermit,
            CARRIER_GB: values.carrierGB,
            WASTE_MANAGEMENT_LICENCE: values.wasteManagementLicense,
            DEFAULT_DISPOSAL_SITE: values.defaultDisposalSite,
            DEFAULT_BRIDGE: values.defaultBridge
          };

          const response = isEdit
            ? await axios.put(`/rest/api/company/info/${currentUser.COMPANY_ID}`, payload)
            : await axios.post('/rest/api/company/info', payload);

          enqueueSnackbar(!isEdit ? 'Create success' : 'Update success', { variant: 'success' });
          navigate('/dashboard/companies');
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar('An error occurred while submitting the form', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      {isDetails ? (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }} alignItems="center">
            {[
              { label: 'Company Name', value: formik.values.companyName || 'N/A' },
              { label: 'Company Reference', value: formik.values.companyReference || 'N/A' },
              { label: 'Main Telephone', value: formik.values.mainTelephone || 'N/A' },
              { label: 'Email', value: formik.values.email || 'N/A' }
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <Card sx={{ display: 'flex', alignItems: 'center', p: 3, maxHeight: 100 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">{item.label}</Typography>
                    <Typography variant="body2">{item.value}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, boxShadow: 3 }}>
                {/* General Information Section */}
                <CardHeader title="Company Details" />
                <Stack spacing={3} padding={3}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: ContactIcons.link,
                        title: 'Company Name',
                        content: formik.values.companyName
                      },
                      {
                        icon: ContactIcons.assignment,
                        title: 'Company Reference',
                        content: formik.values.companyReference
                      },
                      {
                        icon: ContactIcons.locationCity,
                        title: 'Default Company',
                        content: formik.values.defaultCompany ? 'Yes' : 'No'
                      },
                      {
                        icon: ContactIcons.locationCity,
                        title: 'Disposal Site',
                        content: formik.values.disposalSite ? 'Yes' : 'No'
                      }
                    ].map(({ icon, title, content }, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <InfoCard icon={icon} title={title} content={content} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>

                {/* Contact Details Section */}
                <CardHeader title="Contact Information" />
                <Stack spacing={3} padding={3}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: ContactIcons.person,
                        title: 'Main Telephone',
                        content: formik.values.mainContact
                      },
                      {
                        icon: ContactIcons.call,
                        title: 'Alternative Telephone',
                        content: formik.values.mainTelephone
                      },
                      { icon: ContactIcons.email, title: 'Email', content: formik.values.email },
                      {
                        icon: ContactIcons.email,
                        title: 'Website',
                        content: formik.values.website
                      }
                    ].map(({ icon, title, content }, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <InfoCard icon={icon} title={title} content={content} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>

                {/* Address Information Section */}
                <CardHeader title="Address Information" />
                <Stack spacing={3} padding={3}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: ContactIcons.house,
                        title: 'Building Number',
                        content: formik.values.buildingNumber
                      },
                      {
                        icon: ContactIcons.place,
                        title: 'Address Line 1',
                        content: formik.values.addressLine1
                      },
                      {
                        icon: ContactIcons.place,
                        title: 'Address Line 2',
                        content: formik.values.addressLine2
                      },
                      {
                        icon: ContactIcons.pinDrop,
                        title: 'Postcode',
                        content: formik.values.postcode
                      }
                    ].map(({ icon, title, content }, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <InfoCard icon={icon} title={title} content={content} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>

                {/* Additional Details Section */}
                <CardHeader title="Company Registration & Licensing" />
                <Stack spacing={3} padding={3}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: ContactIcons.checkCircle,
                        title: 'VAT Registration Number',
                        content: formik.values.vatRegistrationNumber || 'N/A'
                      },
                      {
                        icon: ContactIcons.group,
                        title: 'Company Registration Number',
                        content: formik.values.companyRegistrationNumber || 'N/A'
                      }
                    ].map(({ icon, title, content }, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <InfoCard icon={icon} title={title} content={content} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
                <CardHeader title="Waste & Environmental Permits" />
                <Stack spacing={3} padding={3}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: ContactIcons.group,
                        title: 'Waste Carrier Number',
                        content: formik.values.wasteCarrierNumber || 'N/A'
                      },
                      {
                        icon: ContactIcons.note,
                        title: 'Waste Broker Number',
                        content: formik.values.wasteBrokerNumber || 'N/A'
                      },
                      {
                        icon: ContactIcons.note,
                        title: 'PPC License Number',
                        content: formik.values.ppcLicenseNumber || 'N/A'
                      },
                      {
                        icon: ContactIcons.note,
                        title: 'Site License Number',
                        content: formik.values.siteLicenseNumber || 'N/A'
                      },
                      {
                        icon: ContactIcons.note,
                        title: 'Waste Collection Permit',
                        content: formik.values.wasteCollectionPermit || 'N/A'
                      },
                      {
                        icon: ContactIcons.note,
                        title: 'Carrier GB',
                        content: formik.values.carrierGB || 'N/A'
                      },
                      {
                        icon: ContactIcons.note,
                        title: 'Waste Management License',
                        content: formik.values.wasteManagementLicense || 'N/A'
                      }
                    ].map(({ icon, title, content }, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <InfoCard icon={icon} title={title} content={content} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
                <CardHeader title="Default Settings" />
                <Stack spacing={3} padding={3}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: ContactIcons.note,
                        title: 'Default Disposal Site',
                        content: formik.values.defaultDisposalSite || 'N/A'
                      },
                      {
                        icon: ContactIcons.note,
                        title: 'Default Bridge',
                        content: formik.values.defaultBridge || 'N/A'
                      }
                    ].map(({ icon, title, content }, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <InfoCard icon={icon} title={title} content={content} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* General Information Section */}
                  <Typography variant="h6" gutterBottom>
                    Company Details
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="Company Name*"
                      {...getFieldProps('companyName')}
                      error={Boolean(touched.companyName && errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                    />
                    <TextField
                      fullWidth
                      label="Company Reference*"
                      {...getFieldProps('companyReference')}
                      error={Boolean(touched.companyReference && errors.companyReference)}
                      helperText={touched.companyReference && errors.companyReference}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">Default Company*</FormLabel>
                      <RadioGroup
                        row
                        {...getFieldProps('defaultCompany')}
                        value={formik.values.defaultCompany ? 'true' : 'false'}
                        onChange={(e) =>
                          formik.setFieldValue('defaultCompany', e.target.value === 'true')
                        }
                      >
                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                        <FormControlLabel value="false" control={<Radio />} label="No" />
                      </RadioGroup>
                      {touched.defaultCompany && errors.defaultCompany && (
                        <FormHelperText error>{errors.defaultCompany}</FormHelperText>
                      )}
                    </FormControl>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">Disposal Site*</FormLabel>
                      <RadioGroup
                        row
                        {...getFieldProps('disposalSite')}
                        value={formik.values.disposalSite ? 'true' : 'false'}
                        onChange={(e) =>
                          formik.setFieldValue('disposalSite', e.target.value === 'true')
                        }
                      >
                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                        <FormControlLabel value="false" control={<Radio />} label="No" />
                      </RadioGroup>
                      {touched.disposalSite && errors.disposalSite && (
                        <FormHelperText error>{errors.disposalSite}</FormHelperText>
                      )}
                    </FormControl>
                  </Stack>

                  {/* Address Information Section */}
                  <Typography variant="h6" gutterBottom>
                    Address Information
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Building Number*"
                      {...getFieldProps('buildingNumber')}
                      error={Boolean(touched.buildingNumber && errors.buildingNumber)}
                      helperText={touched.buildingNumber && errors.buildingNumber}
                    />
                    <TextField
                      fullWidth
                      label="Address Line 1*"
                      {...getFieldProps('addressLine1')}
                      error={Boolean(touched.addressLine1 && errors.addressLine1)}
                      helperText={touched.addressLine1 && errors.addressLine1}
                    />
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      {...getFieldProps('addressLine2')}
                    />
                    <TextField
                      fullWidth
                      label="Postcode*"
                      {...getFieldProps('postcode')}
                      error={Boolean(touched.postcode && errors.postcode)}
                      helperText={touched.postcode && errors.postcode}
                    />
                  </Stack>

                  {/* Contact Information Section */}
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Stack spacing={2} direction="row">
                    <TextField
                      fullWidth
                      label="Main Telephone*"
                      {...getFieldProps('mainTelephone')}
                      error={Boolean(touched.mainTelephone && errors.mainTelephone)}
                      helperText={touched.mainTelephone && errors.mainTelephone}
                    />
                    <TextField
                      fullWidth
                      label="Alternative Telephone"
                      {...getFieldProps('alternativeTelephone')}
                    />
                    <TextField
                      fullWidth
                      label="Alternative Telephone Description"
                      {...getFieldProps('alternativeTelephoneDescription')}
                    />
                  </Stack>
                  <Stack spacing={2} direction="row">
                    <TextField
                      fullWidth
                      label="Mobile No.*"
                      {...getFieldProps('mobileNumber')}
                      error={Boolean(touched.mobileNumber && errors.mobileNumber)}
                      helperText={touched.mobileNumber && errors.mobileNumber}
                    />
                    <TextField
                      fullWidth
                      label="Email*"
                      {...getFieldProps('email')}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                    <TextField
                      fullWidth
                      label="Website*"
                      {...getFieldProps('website')}
                      error={Boolean(touched.website && errors.website)}
                      helperText={touched.website && errors.website}
                    />
                  </Stack>
                  <Stack spacing={2} direction="row">
                    <TextField
                      fullWidth
                      label="Main Contact*"
                      {...getFieldProps('mainContact')}
                      error={Boolean(touched.mainContact && errors.mainContact)}
                      helperText={touched.mainContact && errors.mainContact}
                    />
                  </Stack>

                  {/* Registration Information Section */}
                  <Typography variant="h6" gutterBottom>
                    Company Registration & Licensing
                  </Typography>
                  <Stack spacing={2} direction="row">
                    <TextField
                      fullWidth
                      label="VAT Registration Number"
                      {...getFieldProps('vatRegistrationNumber')}
                    />
                    <TextField
                      fullWidth
                      label="Company Registration Number"
                      {...getFieldProps('companyRegistrationNumber')}
                    />
                  </Stack>
                  <Typography variant="h6" gutterBottom>
                    Waste & Environmental Permits
                  </Typography>
                  <Stack spacing={2} direction="row">
                    <TextField
                      fullWidth
                      label="Waste Carrier Number"
                      {...getFieldProps('wasteCarrierNumber')}
                    />
                    <TextField
                      fullWidth
                      label="Waste Broker Number"
                      {...getFieldProps('wasteBrokerNumber')}
                    />
                    <TextField
                      fullWidth
                      label="PPC License Number"
                      {...getFieldProps('ppcLicenseNumber')}
                    />
                    <TextField
                      fullWidth
                      label="Site License Number"
                      {...getFieldProps('siteLicenseNumber')}
                    />
                  </Stack>
                  <Stack spacing={2} direction="row">
                    <TextField
                      fullWidth
                      label="Waste Collection Permit"
                      {...getFieldProps('wasteCollectionPermit')}
                    />
                    <TextField fullWidth label="Carrier GB" {...getFieldProps('carrierGB')} />
                    <TextField
                      fullWidth
                      label="Waste Management License"
                      {...getFieldProps('wasteManagementLicense')}
                    />
                  </Stack>

                  {/* Default Information Section */}
                  <Typography variant="h6" gutterBottom>
                    Default Settings
                  </Typography>
                  <Stack spacing={2} direction="row">
                    <TextField
                      fullWidth
                      label="Default Disposal Site"
                      {...getFieldProps('defaultDisposalSite')}
                    />
                    <TextField
                      fullWidth
                      label="Default Bridge"
                      {...getFieldProps('defaultBridge')}
                    />
                  </Stack>

                  {/* Submit Button */}
                  <Stack direction="row" justifyContent="flex-end">
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={isSubmitting}
                        disabled={
                          !isEdit && // If it's not in edit mode
                          ((!isDetails && !formik.isValid) || // If validation is required and form is invalid
                            formik.isSubmitting ||
                            Object.keys(formik.errors).length > 0)
                        }
                      >
                        {!isEdit ? 'Create Company' : 'Save Changes'}
                      </LoadingButton>
                    </Box>
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Form>
      )}
    </FormikProvider>
  );
}
